/**
 * useExecutorAttachments — o estado dos anexos do turno no compositor.
 *
 * Um chip por arquivo, com quatro estados visíveis: enviando → lendo → pronto,
 * ou falhou com motivo. Nenhum estado intermediário é escondido: um anexo que
 * demora é um anexo que o usuário está olhando.
 *
 * ── POR QUE ESTE ARQUIVO NÃO USA UPDATER DE setState ───────────────────────
 * A primeira versão fazia `setChips(prev => { ...; localId = crypto.randomUUID();
 * return [...prev, chip] })` e lia `localId` na linha seguinte. Isso quebrou em
 * produção de um jeito perfeitamente silencioso, e a lição vale escrita:
 *
 *   React 18 só executa o updater SINCRONAMENTE pelo caminho de "eager state",
 *   que exige a fiber sem lanes pendentes (dispatchSetState). Depois do
 *   primeiro commit do componente — ou seja, quase sempre, e sempre a partir
 *   do segundo arquivo do mesmo laço — o updater é adiado para a fase de
 *   render. A variável de fora ainda vale "" quando o código a lê.
 *
 * Consequência medida: o chip entrava na lista (o updater roda depois) mas o
 * pipeline nunca era chamado. Zero bytes no Storage, zero linhas no banco,
 * zero exceções, zero Sentry, e o chip em "enviando…" para sempre. O mesmo
 * defeito atingia `rejected`: um .exe recusado não virava chip nenhum.
 *
 * A regra desta casa agora: **o updater de setState é sempre uma função pura
 * de `prev`, ou não existe.** Aqui não existe — `chipsRef` é a fonte síncrona
 * e `commit()` mantém ref e state casados. Ler `chipsRef.current` é sempre
 * ler o AGORA, sem depender de quando o React resolve render.
 *
 * ── O CICLO DE VIDA É O CRITÉRIO ───────────────────────────────────────────
 * Cada caso abaixo é um caminho de código deste arquivo, não uma observação:
 *
 *  · cancelado NO MEIO do upload — `FileOptions` do storage-js não tem
 *    `signal`, então a transferência NÃO é abortável. O cancelamento é
 *    COMPENSATÓRIO: o chip some na hora, o upload termina atrás e o objeto é
 *    apagado ao aterrissar (uploadFile.ts cuida disso).
 *  · nada nunca fica sem prazo — upload, registro e leitura têm deadline
 *    próprio, e um watchdog fecha qualquer chip que escape dos três. Um chip
 *    preso não trava só o anexo: `busy` desabilita o botão de enviar, então
 *    um spinner eterno congelava a CONVERSA inteira.
 *  · removido antes de enviar — linha e bytes vão JUNTOS.
 *  · saiu da tela com o ingest em voo — a LINHA continua em `pending` e quem
 *    fecha é o varredor (executor-sweep-pending).
 *  · upload OK e registro falhou — apaga o objeto AGORA e mostra o erro AGORA.
 *  · registro OK e o ingest falhou — a linha fica `pending` e o chip mostra
 *    "falhou" com "tentar de novo" (o ingest é idempotente por D4).
 *  · exceção DEPOIS do registro — linha E bytes saem juntos. Apagar só os
 *    bytes deixava um órfão invertido: linha viva apontando para nada, que o
 *    varredor reprocessaria até esgotar as tentativas.
 *  · mesmo arquivo duas vezes no mesmo turno — recusado com motivo próprio.
 *  · anexo `ready` cujo objeto sumiu por fora — o turno NÃO quebra.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { executorClient } from "@/lib/executorDatabase";
import {
  MAX_ATTACHMENTS_PER_TURN,
  buildStoragePath,
  preflightAttachment,
  type AttachmentKind,
  type PreflightReason,
} from "@/lib/executorUpload";
import {
  removeFromBucket,
  uploadDeadlineMs,
  uploadToBucket,
  UploadAbortedError,
  UploadTimeoutError,
} from "@/lib/uploadFile";
import { assertConnectedFileImportGuard, type ConnectedFileImportGuard } from "@/lib/connectedFiles";
import { connectedFileReadSessionId } from "@/lib/connectedFileRead";
import type { WhatsAppReviewStage } from "@/lib/mirrorWhatsAppReview";
import type { MirrorAuthLease, MirrorAuthSessionScope } from "@/lib/mirrorAuthSession";
import { useExecutorCaps } from "@/hooks/useExecutorCaps";

const LOG = "[useExecutorAttachments]";
const BUCKET = "executor-inputs";

/** Prazo do registro. É um INSERT — segundos, nunca minutos. */
const REGISTER_DEADLINE_MS = 20_000;
/** Prazo da leitura. Planilha grande leva alguns segundos no ExcelJS. */
const INGEST_DEADLINE_MS = 120_000;
/**
 * Rede de segurança da UI. Se, apesar dos três prazos acima, um chip ficar
 * ocupado além disto, ele é fechado como falha. Existe porque um chip preso
 * não custa só o anexo: `busy` desliga o botão de enviar, e a conversa inteira
 * congela junto. Folgado de propósito — só pega o que escapou de tudo.
 */
const CHIP_WATCHDOG_MS = 6 * 60_000;

export type AttachmentChipStatus = "uploading" | "ingesting" | "ready" | "failed";

export interface AttachmentChip {
  /** Id local do chip. Existe antes de a linha existir — é ele que permite
   *  cancelar durante o upload, quando ainda não há id de banco. */
  localId: string;
  fileName: string;
  byteSize: number;
  status: AttachmentChipStatus;
  /** Id de `executor_attachments`, a partir do registro. */
  attachmentId: string | null;
  storagePath: string | null;
  /** Código do vocabulário fechado — vira copy por failureReasonI18nKey. */
  reason: PreflightReason | string | null;
  kind: AttachmentKind | null;
  /** Quando o chip entrou em estado ocupado. Só o watchdog lê. */
  busySince: number | null;
  /** Existing WhatsApp file: remove is a local detach, never a storage delete. */
  referenced?: boolean;
}

interface RetryPayload {
  file: File;
  importGuard?: ConnectedFileImportGuard;
}

/** Promise com prazo. Rejeita com o erro dado quando o tempo estoura. */
function withDeadline<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new Error(label + " timed out after " + String(ms) + "ms")), ms);
  });
  return Promise.race([promise, deadline]).finally(() => {
    if (timer) clearTimeout(timer);
  }) as Promise<T>;
}

export function useExecutorAttachments() {
  const caps = useExecutorCaps();
  const [chips, setChips] = useState<AttachmentChip[]>([]);
  /**
   * Espelho SÍNCRONO do state. É daqui que todo código lê — nunca do `chips`
   * capturado por closure nem de dentro de um updater. Ver o cabeçalho.
   */
  const chipsRef = useRef<AttachmentChip[]>([]);
  const composerRevisionRef = useRef(0);
  /** Cancelamentos em voo, por chip. */
  const abortersRef = useRef<Map<string, AbortController>>(new Map());
  /** Arquivo original de cada chip, para o "tentar de novo" não pedir de novo. */
  const filesRef = useRef<Map<string, RetryPayload>>(new Map());
  const importGuardsRef = useRef(new Map<string, ConnectedFileImportGuard>());
  const ownerRef = useRef<string | null | undefined>(undefined);
  const generationRef = useRef(0);
  const reviewRef = useRef<{ stage: WhatsAppReviewStage; scope: MirrorAuthSessionScope; lease: MirrorAuthLease } | null>(null);

  /** Única porta de escrita: mantém ref e state sempre casados. */
  const commit = useCallback((next: AttachmentChip[]) => {
    composerRevisionRef.current++;
    chipsRef.current = next;
    setChips(next);
  }, []);

  const patch = useCallback(
    (localId: string, next: Partial<AttachmentChip>) => {
      commit(
        chipsRef.current.map((c) => {
          if (c.localId !== localId) return c;
          const merged = { ...c, ...next };
          // O relógio do watchdog reinicia a cada transição de estado ocupado
          // e para quando o chip fecha.
          if (next.status !== undefined) {
            merged.busySince = next.status === "uploading" || next.status === "ingesting"
              ? Date.now()
              : null;
          }
          return merged;
        }),
      );
    },
    [commit],
  );

  const capsInvalidate = caps.invalidate;


  /** Linha + bytes saem juntos. Usado no cancelamento, na remoção e no catch. */
  const discardRow = useCallback(async (attachmentId: string, storagePath: string | null) => {
    const { data, error } = await executorClient
      .from("executor_attachments")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", attachmentId)
      .select("id");
    // §4.9 — 0 linhas significa que a RLS barrou em silêncio. Loga, mas segue
    // para apagar os bytes: deixar o objeto para trás seria o pior dos dois.
    if (error || !data || data.length === 0) {
      console.error(LOG + " soft delete matched no row id=" + attachmentId, error);
    }
    if (storagePath) await removeFromBucket(BUCKET, [storagePath]);
  }, []);

  useEffect(() => {
    const aborters = abortersRef.current, files = filesRef.current;
    const invalidateLookups = () => { generationRef.current++; };
    const reset = () => {
      invalidateLookups();
      aborters.forEach(controller => controller.abort());
      aborters.clear(); files.clear(); importGuardsRef.current.clear(); reviewRef.current = null; commit([]);
    };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = session?.user.id ?? null;
      if (ownerRef.current !== undefined && ownerRef.current !== next) reset();
      ownerRef.current = next;
      const review = reviewRef.current;
      if (review && (session?.user.id !== review.lease.ownerId || connectedFileReadSessionId(session.access_token) !== review.lease.authSessionId)) {
        reviewRef.current = null;
        commit(chipsRef.current.filter(chip => !chip.referenced));
      }
      for (const [localId, guard] of importGuardsRef.current) {
        if (session?.user.id === guard.origin.ownerId && connectedFileReadSessionId(session.access_token) === guard.origin.authSessionId) continue;
        aborters.get(localId)?.abort(); files.delete(localId); importGuardsRef.current.delete(localId);
        const chip = chipsRef.current.find(item => item.localId === localId);
        commit(chipsRef.current.filter(item => item.localId !== localId));
        if (chip?.attachmentId && !aborters.has(localId)) void discardRow(chip.attachmentId, chip.storagePath);
      }
    });
    // Navigation preserves started ingest; connected imports also cancel on a login change.
    return () => { invalidateLookups(); subscription.unsubscribe(); };
  }, [commit, discardRow]);

  // Synchronous all-or-none commit: no upload, ingest, row write or quota debit.
  const stageReferences = useCallback((stage: WhatsAppReviewStage, scope: MirrorAuthSessionScope, lease: MirrorAuthLease) => {
    if (!scope.isCurrent(lease) || stage.ownerId !== lease.ownerId || stage.expiresAt <= Date.now() ||
      chipsRef.current.length || abortersRef.current.size || stage.attachments.length > MAX_ATTACHMENTS_PER_TURN) return false;
    const ids = stage.attachments.map(row => row.id);
    if (new Set(ids).size !== ids.length) return false;
    const next: AttachmentChip[] = stage.attachments.map(row => ({
      localId: crypto.randomUUID(), fileName: row.fileName, byteSize: row.byteSize, status: "ready",
      attachmentId: row.id, storagePath: null, kind: row.kind, reason: null, busySince: null, referenced: true,
    }));
    reviewRef.current = next.length ? { stage, scope, lease } : null;
    commit(next);
    return true;
  }, [commit]);
  const composerRevision = useCallback(() => composerRevisionRef.current, []);
  const composerEmpty = useCallback(() => chipsRef.current.length === 0 && abortersRef.current.size === 0, []);
  // Expiration/revocation never silently turns a bridge send into an ordinary send.
  const reviewSelection = useCallback((ids: string[]) => {
    const review = reviewRef.current;
    if (!review) return undefined;
    const refs = chipsRef.current.filter(chip => chip.referenced);
    if (!refs.length) { reviewRef.current = null; return undefined; }
    if (!review.scope.isCurrent(review.lease) || review.stage.expiresAt <= Date.now() ||
      ids.length < 1 || new Set(ids).size !== ids.length ||
      ids.some(id => !refs.some(chip => chip.status === "ready" && chip.attachmentId === id))) throw new Error("whatsapp_review_unavailable");
    return { messageId: review.stage.messageId, expiresAt: review.stage.expiresAt, ownerId: review.stage.ownerId };
  }, []);

  const clearReview = useCallback((messageId: string) => {
    if (reviewRef.current?.stage.messageId !== messageId) return;
    reviewRef.current = null;
    commit(chipsRef.current.filter(chip => !chip.referenced));
  }, [commit]);

  /** Traduz a resposta do executor-ingest em patch de chip. */
  const applyIngestResult = useCallback(
    (localId: string, data: unknown) => {
      const result = (data ?? {}) as { status?: unknown; kind?: unknown; failure_reason?: unknown };
      if (result.status === "ready") {
        patch(localId, {
          status: "ready",
          kind: typeof result.kind === "string" ? (result.kind as AttachmentKind) : null,
          reason: null,
        });
        return;
      }
      patch(localId, {
        status: "failed",
        reason: typeof result.failure_reason === "string" ? result.failure_reason : "internal_error",
      });
    },
    [patch],
  );

  /**
   * O client perdeu a paciência, mas o SERVIDOR pode ter concluído. Antes de
   * dizer "falhou", pergunta ao banco como a linha ficou — senão um ingest
   * lento vira erro na cara do usuário sobre um arquivo que está pronto.
   */
  const reconcileAfterIngestTimeout = useCallback(
    async (localId: string, attachmentId: string) => {
      const { data, error } = await executorClient
        .from("executor_attachments")
        .select("status, kind, failure_reason")
        .eq("id", attachmentId)
        .maybeSingle();
      if (error || !data) {
        console.error(LOG + " reconcile failed id=" + attachmentId, error);
        patch(localId, { status: "failed", reason: "ingest_unreachable" });
        return;
      }
      const row = data;
      if (row.status === "ready" || row.status === "failed") {
        applyIngestResult(localId, {
          status: row.status,
          kind: row.kind,
          failure_reason: row.failure_reason,
        });
        return;
      }
      // Ainda `pending`: o varredor server-side assume daqui. O chip mostra
      // falha retentável — o usuário não espera 15 minutos olhando spinner.
      console.error(LOG + " ingest still pending after deadline id=" + attachmentId);
      patch(localId, { status: "failed", reason: "ingest_unreachable" });
    },
    [patch, applyIngestResult],
  );

  /**
   * Sobe → registra → manda ler. Devolve nada: quem acompanha é o chip.
   *
   * Cada etapa fecha o próprio rastro em caso de falha. É repetitivo de
   * propósito: um `finally` genérico não sabe se o objeto chegou a existir, e
   * apagar o que não existe esconde o erro que importa.
   */
  const runPipeline = useCallback(
    async (localId: string, file: File, userId: string, importGuard?: ConnectedFileImportGuard) => {
      const controller = new AbortController();
      abortersRef.current.set(localId, controller);
      const abortImport = () => controller.abort();
      importGuard?.signal.addEventListener("abort", abortImport, { once: true });
      const assertImport = async () => { if (importGuard) await assertConnectedFileImportGuard(importGuard, controller.signal); };

      let storagePath: string | null = null;
      let attachmentId: string | null = null;
      try {
        await assertImport();
        storagePath = buildStoragePath(userId, file.name, new Date());
        await uploadToBucket({
          bucket: BUCKET,
          path: storagePath,
          file: file,
          signal: controller.signal,
          deadlineMs: uploadDeadlineMs(file.size),
        });
        if (controller.signal.aborted) return; // uploadToBucket já recolheu os bytes
        await assertImport();

        patch(localId, { status: "ingesting", storagePath: storagePath });

        // O client NÃO pode dar INSERT em executor_attachments (a NANO revogou
        // de propósito). O registro é uma RPC SECURITY DEFINER que valida a
        // pasta e lê o tamanho REAL de storage.objects.
        const { data: registered, error: registerError } = await withDeadline(
          Promise.resolve(
            executorClient.rpc("executor_attachment_register", {
              p_storage_path: storagePath,
              p_original_filename: file.name,
            }),
          ),
          REGISTER_DEADLINE_MS,
          "register",
        );
        if (registerError || typeof registered !== "string" || !registered) {
          console.error(LOG + " register failed path=" + storagePath, registerError);
          // Objeto sem linha = órfão. O varredor pegaria em 1h; aqui é agora.
          await removeFromBucket(BUCKET, [storagePath]);
          patch(localId, { status: "failed", reason: "register_failed", storagePath: null });
          return;
        }

        attachmentId = registered;
        await assertImport();
        patch(localId, { attachmentId: attachmentId });
        if (controller.signal.aborted) {
          await discardRow(attachmentId, storagePath);
          return;
        }

        let ingested: unknown;
        try {
          const res = await withDeadline(
            supabase.functions.invoke("executor-ingest", {
              body: { attachment_id: attachmentId },
            }),
            INGEST_DEADLINE_MS,
            "ingest",
          );
          await assertImport();
          if (res.error) {
            // A LINHA fica em `pending`: o varredor reprocessa. O chip mostra
            // falha com "tentar de novo" — o usuário não espera 15 min.
            console.error(LOG + " ingest invoke failed id=" + attachmentId, res.error);
            patch(localId, { status: "failed", reason: "ingest_unreachable" });
            return;
          }
          ingested = res.data;
        } catch (ingestErr) {
          if (importGuard) await assertImport();
          console.error(LOG + " ingest deadline id=" + attachmentId, ingestErr);
          await reconcileAfterIngestTimeout(localId, attachmentId);
          return;
        }

        if (controller.signal.aborted) {
          await discardRow(attachmentId, storagePath);
          return;
        }

        await assertImport();
        applyIngestResult(localId, ingested);
      } catch (err) {
        if (err instanceof UploadAbortedError) return; // cancelamento, não falha
        const timedOut = err instanceof UploadTimeoutError;
        console.error(LOG + " pipeline " + (timedOut ? "timed out" : "threw"), err);
        // Se a linha já existe, ela sai JUNTO com os bytes. Apagar só os bytes
        // deixava um órfão invertido — linha viva apontando para nada — que o
        // varredor reprocessaria até esgotar as tentativas, e o "tentar de
        // novo" do usuário reprocessaria para sempre um objeto inexistente.
        if (attachmentId) {
          await discardRow(attachmentId, storagePath);
        } else if (storagePath) {
          await removeFromBucket(BUCKET, [storagePath]);
        }
        if (importGuard && (controller.signal.aborted || err instanceof Error && ["account_changed", "connection_changed"].includes(err.message))) {
          filesRef.current.delete(localId); importGuardsRef.current.delete(localId);
          commit(chipsRef.current.filter(chip => chip.localId !== localId));
          return;
        }
        patch(localId, {
          status: "failed",
          // Motivo HONESTO: `upload_failed` para uma falha depois do upload
          // mentiria e mandaria o usuário conferir a conexão à toa.
          reason: timedOut ? "upload_timeout" : attachmentId ? "ingest_unreachable" : "upload_failed",
          storagePath: null,
          attachmentId: null,
        });
      } finally {
        importGuard?.signal.removeEventListener("abort", abortImport);
        abortersRef.current.delete(localId);
        // Aceito ou recusado, a contagem do dia pode ter mudado — e é ela que
        // decide o próximo erro rápido de cota.
        capsInvalidate();
      }
    },
    [patch, capsInvalidate, discardRow, applyIngestResult, reconcileAfterIngestTimeout, commit],
  );

  /**
   * Ponto de entrada do <input type="file">. Valida ANTES de gastar banda.
   *
   * TUDO aqui é síncrono contra `chipsRef.current`: nenhuma decisão sai de
   * dentro de um updater. É o conserto do bug que travava o chip em
   * "enviando…" — ver o cabeçalho do arquivo.
   */
  const attach = useCallback(
    async (files: FileList | File[], expectedOwnerId?: string, importGuard?: ConnectedFileImportGuard) => {
      const generation = generationRef.current;
      let loginChanged = false;
      const subscription = importGuard ? supabase.auth.onAuthStateChange((_event, session) => {
        if (session?.user.id !== importGuard.origin.ownerId || connectedFileReadSessionId(session.access_token) !== importGuard.origin.authSessionId) loginChanged = true;
      }).data.subscription : undefined;
      const assertImport = async () => {
        if (importGuard) await assertConnectedFileImportGuard(importGuard);
        if (loginChanged) throw new Error("account_changed");
      };
      try {
        const list = Array.from(files);
        if (list.length === 0) return;

        await assertImport();
        const {
          data: { user },
        } = await supabase.auth.getUser();
        await assertImport();
        if (!user || generation !== generationRef.current || expectedOwnerId && user.id !== expectedOwnerId || ownerRef.current !== undefined && ownerRef.current !== user.id) {
          console.error(LOG + " attach without matching session — nenhum chip criado");
          return;
        }

        ownerRef.current = user.id;
        const created: { localId: string; file: File }[] = [];
        const importedLocalIds: string[] = [];
        // Uma cópia de trabalho: o terceiro arquivo do laço tem de enxergar os
        // dois que acabaram de entrar, e state não atualiza no meio do laço.
        let working = [...chipsRef.current];

        for (const file of list) {
          const already = working.some(
            (c) => c.fileName === file.name && c.byteSize === file.size && c.status !== "failed",
          );
          const live = working.filter((c) => c.status !== "failed").length;

          let rejected: PreflightReason | null = null;
          if (already) {
            rejected = "duplicate_attachment";
          } else if (!caps.loading) {
            // A validação de tetos só vale quando os caps REAIS chegaram: no
            // primeiro frame o estado é o fallback de free, e um Premium com
            // 12 MB legítimos levaria "grande demais" por causa de um race.
            const decision = preflightAttachment(
              { fileName: file.name, byteSize: file.size, attachedThisTurn: live },
              caps,
            );
            if (decision.ok === false) rejected = decision.reason;
          } else if (live >= (caps.maxPerTurn || MAX_ATTACHMENTS_PER_TURN)) {
            rejected = "too_many_attachments";
          }

          const localId = crypto.randomUUID();
          importedLocalIds.push(localId);
          if (rejected) {
            // Rejeição vira chip FALHADO em vez de toast solto: o usuário vê
            // QUAL arquivo foi recusado quando escolheu vários. E loga — §11:
            // recusar em silêncio é indistinguível de não fazer nada.
            console.error(LOG + " preflight rejected file=" + file.name + " reason=" + rejected);
            working = [
              ...working,
              {
                localId,
                fileName: file.name,
                byteSize: file.size,
                status: "failed",
                attachmentId: null,
                storagePath: null,
                reason: rejected,
                kind: null,
                busySince: null,
              },
            ];
            continue;
          }

          working = [
            ...working,
            {
              localId,
              fileName: file.name,
              byteSize: file.size,
              status: "uploading",
              attachmentId: null,
              storagePath: null,
              reason: null,
              kind: null,
              busySince: Date.now(),
            },
          ];
          created.push({ localId, file });
        }

        if (importGuard) {
          for (const localId of importedLocalIds) importGuardsRef.current.set(localId, importGuard);
          importGuard.onStarted?.();
        }
        commit(working);
        // Só DEPOIS de o state estar consistente os pipelines partem — assim
        // qualquer patch() que chegue rápido encontra o chip já no lugar.
        for (const { localId, file } of created) {
          filesRef.current.set(localId, { file, ...(importGuard ? { importGuard } : {}) });
          void runPipeline(localId, file, user.id, importGuard);
        }
      } finally { subscription?.unsubscribe(); }
    },
    [caps, runPipeline, commit],
  );

  /** Remove um chip — e leva linha e bytes junto quando eles já existem. */
  const remove = useCallback(
    async (localId: string) => {
      const chip = chipsRef.current.find((c) => c.localId === localId);
      commit(chipsRef.current.filter((c) => c.localId !== localId));
      filesRef.current.delete(localId); importGuardsRef.current.delete(localId);

      const aborter = abortersRef.current.get(localId);
      if (aborter) aborter.abort(); // upload em voo → limpeza compensatória

      if (chip?.referenced) {
        if (!chipsRef.current.some(item => item.referenced)) reviewRef.current = null;
        return;
      }
      if (chip?.attachmentId) {
        await discardRow(chip.attachmentId, chip.storagePath);
        capsInvalidate();
      } else if (chip?.storagePath && !aborter) {
        await removeFromBucket(BUCKET, [chip.storagePath]);
      }
    },
    [commit, capsInvalidate, discardRow],
  );

  /** Tentar de novo — reusa o arquivo que já está na memória. */
  const retry = useCallback(
    async (localId: string) => {
      const payload = filesRef.current.get(localId);
      const chip = chipsRef.current.find((c) => c.localId === localId);
      if (!chip || chip.referenced) return;
      const importGuard = payload?.importGuard ?? importGuardsRef.current.get(localId);
      const assertRetry = async () => {
        if (!importGuard) return;
        try { await assertConnectedFileImportGuard(importGuard); }
        catch (cause) {
          filesRef.current.delete(localId); importGuardsRef.current.delete(localId);
          commit(chipsRef.current.filter(item => item.localId !== localId));
          if (chip.attachmentId) await discardRow(chip.attachmentId, chip.storagePath);
          throw cause;
        }
      };
      try { await assertRetry(); } catch { return; }
      const {
        data: { user },
      } = await supabase.auth.getUser();
      try { await assertRetry(); } catch { return; }
      if (!user || importGuard && user.id !== importGuard.origin.ownerId) {
        console.error(LOG + " retry without session id=" + localId);
        return;
      }

      // Anexo que já tem linha reprocessa PELO id (o ingest é idempotente);
      // sem linha, refaz o caminho inteiro — e aí precisa do arquivo.
      if (chip.attachmentId) {
        patch(localId, { status: "ingesting", reason: null });
        try {
          const res = await withDeadline(
            supabase.functions.invoke("executor-ingest", {
              body: { attachment_id: chip.attachmentId },
            }),
            INGEST_DEADLINE_MS,
            "ingest",
          );
          await assertRetry();
          if (res.error) {
            console.error(LOG + " retry ingest failed id=" + chip.attachmentId, res.error);
            patch(localId, { status: "failed", reason: "ingest_unreachable" });
            return;
          }
          applyIngestResult(localId, res.data);
        } catch (err) {
          if (importGuard) { try { await assertRetry(); } catch { return; } }
          console.error(LOG + " retry ingest deadline id=" + chip.attachmentId, err);
          await reconcileAfterIngestTimeout(localId, chip.attachmentId);
        } finally {
          capsInvalidate();
        }
        return;
      }

      if (!payload) {
        // O arquivo não sobreviveu (aba recarregada). Sem ele não há o que
        // reenviar — e dizer isso é melhor que um botão que não faz nada.
        console.error(LOG + " retry without file in memory id=" + localId);
        patch(localId, { status: "failed", reason: "upload_failed" });
        return;
      }

      patch(localId, { status: "uploading", reason: null, storagePath: null });
      void runPipeline(localId, payload.file, user.id, importGuard);
    },
    [patch, runPipeline, capsInvalidate, applyIngestResult, reconcileAfterIngestTimeout, commit, discardRow],
  );

  /**
   * Watchdog da UI. Rede de segurança contra qualquer caminho que escape dos
   * três prazos — inclusive um que ainda não exista. Um chip preso desliga o
   * botão de enviar (`busy`), então isto protege a CONVERSA, não só o anexo.
   */
  useEffect(() => {
    const stuck = chips.filter(
      (c) => (c.status === "uploading" || c.status === "ingesting") && c.busySince !== null,
    );
    if (stuck.length === 0) return;
    const now = Date.now();
    const soonest = Math.min(...stuck.map((c) => (c.busySince as number) + CHIP_WATCHDOG_MS - now));
    const timer = window.setTimeout(() => {
      const expired = chipsRef.current.filter(
        (c) =>
          (c.status === "uploading" || c.status === "ingesting") &&
          c.busySince !== null &&
          Date.now() - c.busySince >= CHIP_WATCHDOG_MS,
      );
      if (expired.length === 0) return;
      for (const c of expired) {
        console.error(LOG + " watchdog closing stuck chip file=" + c.fileName + " status=" + c.status);
      }
      const expiredIds = new Set(expired.map((c) => c.localId));
      commit(
        chipsRef.current.map((c) =>
          expiredIds.has(c.localId)
            ? { ...c, status: "failed" as const, reason: "upload_timeout", busySince: null }
            : c,
        ),
      );
    }, Math.max(soonest, 0) + 500);
    return () => window.clearTimeout(timer);
  }, [chips, commit]);

  /**
   * Limpa o compositor depois do envio. NÃO apaga nada: a partir daqui os
   * anexos pertencem ao turno enviado (e à Biblioteca).
   */
  const clear = useCallback(() => {
    abortersRef.current.clear();
    filesRef.current.clear(); importGuardsRef.current.clear(); reviewRef.current = null;
    commit([]);
  }, [commit]);

  /** Ids prontos, na ordem em que o usuário anexou — a mesma ordem dos blocos. */
  const readyIds = chips
    .filter((c) => c.status === "ready" && c.attachmentId)
    .map((c) => c.attachmentId as string);

  /** Há algo em voo? O envio espera — mandar o turno sem o anexo que o usuário
   *  acabou de escolher é pior do que esperar dois segundos. */
  const busy = chips.some((c) => c.status === "uploading" || c.status === "ingesting");

  return { chips, attach, remove, retry, clear, readyIds, busy, caps, stageReferences, composerEmpty, composerRevision, reviewSelection, clearReview };
}
